import { beginLoading, endLoading } from '@app/AppLayout/LoadingContext';
import { useNotification } from '@app/AppLayout/NotificationContext';
import { DetailPageHeader } from '@app/components/DetailPageHeader';
import { NotFoundState } from '@app/components/NotFoundState';
import { EntityChangeLog } from '@app/components/EntityChangeLog';
import { PageActionsMenu } from '@app/components/PageActionsMenu';
import { PhoneInput, isSamePhoneNumber } from '@app/components/PhoneInput';
import { isNotFound } from '@app/utils/ApiError';
import { useBreadcrumbLabel } from '@app/hooks/useBreadcrumbLabel';
import { useEntity } from '@app/hooks/useEntity';
import { ClientExecutive } from '@app/model/ClientExecutive';
import { useClientExecutiveStore, useRedHatRegionStore } from '@app/store/entityStore';
import {
  ActionList,
  ActionListItem,
  Alert,
  AlertVariant,
  Button,
  Card,
  Form,
  FormAlert,
  FormGroup,
  Label,
  LabelGroup,
  MenuToggle,
  MenuToggleElement,
  PageSection,
  Select,
  SelectOption,
  Stack,
  TextInput,
  Title
} from '@patternfly/react-core';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

type ValidationState = 'success' | 'error' | 'default';

const emptyClientExecutive: Partial<ClientExecutive> = {
  name: '',
  username: '',
  email: '',
  mobile: '',
  regions: []
};

/** The fields the record cannot be saved without. */
const requiredFields = ['name', 'username', 'email', 'mobile'] as const;

const pristineValidation: Record<string, ValidationState> = {
  name: 'default', username: 'default', email: 'default', mobile: 'default'
};

const validateField = (field: string, value: string): ValidationState => {
  if (field === 'email') return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value) ? 'success' : 'error';
  return value.trim().length > 0 ? 'success' : 'error';
};

const ClientExecutiveDetail: React.FunctionComponent = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addNotification } = useNotification();

  const [clientExecutive, setClientExecutive] = React.useState<Partial<ClientExecutive>>(emptyClientExecutive);
  const [changeLogOpen, setChangeLogOpen] = React.useState<boolean>(false);
  const [validated, setValidated] = React.useState<Record<string, ValidationState>>(pristineValidation);
  const [isDirty, setIsDirty] = React.useState(false);
  const [isRegionDropdownOpen, setIsRegionDropdownOpen] = React.useState(false);
  /** No client executive answers to that id, so the page says so in place of the record. */
  const [notFound, setNotFound] = React.useState(false);

  const loadClientExecutive = useClientExecutiveStore(state => state.loadById);
  const saveClientExecutive = useClientExecutiveStore(state => state.save);
  const removeClientExecutive = useClientExecutiveStore(state => state.delete);

  const { items: redHatRegions } = useEntity(useRedHatRegionStore);

  useBreadcrumbLabel(id === 'new' ? 'New' : clientExecutive.name);
  // The overview is unmounted, so this page has to name the crumb above it as well
  useBreadcrumbLabel('Client Executives', '/clientexecutives');

  React.useEffect(() => {
    let isMounted = true;
    const loadData = async () => {
      if (id === 'new') return;

      setNotFound(false);
      beginLoading();
      try {
        const ce = await loadClientExecutive(id as string);
        // Cloned so editing the form does not mutate the record held in the store
        if (isMounted) setClientExecutive(structuredClone(ce));
      } catch (error: any) {
        // An id matching no record is the answer rather than a failure: the page renders it.
        if (!isMounted) return;
        if (isNotFound(error)) setNotFound(true);
        else addNotification('Error loading client executive', AlertVariant.danger, error.message);
      } finally {
        endLoading();
      }
    };
    loadData();
    return () => { isMounted = false; };
  }, [id]);

  /** Generic input handler */
  const handleChange = (field: string, value: string) => {
    setValidated(prev => ({ ...prev, [field]: validateField(field, value) }));
    setClientExecutive(prev => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  /** Reformatting by the phone input itself is not an edit, and must not dirty the form */
  const handleMobileChange = (mobile: string) => {
    if (isSamePhoneNumber(mobile, clientExecutive.mobile)) {
      setClientExecutive(prev => ({ ...prev, mobile }));
      return;
    }
    handleChange('mobile', mobile);
  };

  /** Multi-select Region handler: selecting an already selected region removes it */
  const onRegionSelect = (_event: React.MouseEvent | React.ChangeEvent | undefined, selection: string | number | undefined) => {
    if (!selection) return;
    const selectedId = Number(selection);
    const selectedRegion = redHatRegions.find(r => r.id === selectedId);
    if (!selectedRegion) return;

    const currentRegions = clientExecutive.regions || [];
    const isAlreadySelected = currentRegions.some(r => r.id === selectedId);

    setClientExecutive({
      ...clientExecutive,
      regions: isAlreadySelected
        ? currentRegions.filter(r => r.id !== selectedId)
        : [...currentRegions, selectedRegion]
    });
    setIsDirty(true);
  };

  /** Validate every required field, so a save cannot slip through a field never touched */
  const isValid = () => {
    const newValidated: Record<string, ValidationState> = { ...validated };

    requiredFields.forEach(field => {
      newValidated[field] = validateField(field, (clientExecutive as any)[field] || '');
    });

    setValidated(newValidated);
    return !Object.values(newValidated).includes('error');
  };

  const onSaveClick = async () => {
    if (!isValid()) return;

    beginLoading();
    try {
      const isNew = id === 'new';
      const savedData = await saveClientExecutive(clientExecutive, isNew);

      setClientExecutive(savedData);
      setValidated(pristineValidation);
      setIsDirty(false);
      addNotification('Client executive saved successfully', AlertVariant.success);

      if (isNew) navigate(`/clientexecutives/${savedData.id}`, { replace: true });
    } catch (error: any) {
      addNotification('Error saving client executive', AlertVariant.danger, error.message);
    } finally {
      endLoading();
    }
  };

  const onCancelClick = async () => {
    if (id === 'new') {
      setClientExecutive(emptyClientExecutive);
    } else {
      const ce = await loadClientExecutive(id as string);
      setClientExecutive(structuredClone(ce));
    }
    setValidated(pristineValidation);
    setIsDirty(false);
  };

  const onRefresh = async () => {
    if (id === 'new') return;
    const fresh = await loadClientExecutive(id as string, true);
    setClientExecutive(structuredClone(fresh));
    setValidated(pristineValidation);
    setIsDirty(false);
  };

  const onRemove = async () => {
    beginLoading();
    try {
      await removeClientExecutive(clientExecutive.id as number);
      addNotification(`Client Executive "${clientExecutive.name}" removed successfully`, AlertVariant.success);
      setIsDirty(false);
      // Same state the layout's Back button carries, so the list comes back filtered as it was
      navigate('/clientexecutives', { state: { restoreFilters: true } });
    } catch (error: any) {
      addNotification('Error removing client executive', AlertVariant.danger, error.message);
    } finally {
      endLoading();
    }
  };

  if (notFound) {
    return (
      <NotFoundState
        entityLabel="client executive"
        identifier={id}
        backTo={{ path: '/clientexecutives', label: 'Back to client executives' }}
      />
    );
  }

  const changeLogTitle = clientExecutive.name;
  /**
   * Offered only once the record is stored: a form that has never been saved has no revision to
   * read, and the endpoint would answer 404 for the id it does not yet have.
   */
  const changeLogItems = clientExecutive.id
    ? [{ key: 'change-log', label: 'Change log', onClick: () => setChangeLogOpen(true) }]
    : [];


  return (
    <EntityChangeLog
      entity="clientexecutive"
      id={clientExecutive.id}
      entityLabel="this client executive"
      title={changeLogTitle}
      isOpen={changeLogOpen}
      onClose={() => setChangeLogOpen(false)}
    >
      <DetailPageHeader
        title={id === 'new' ? 'New Client Executive' : clientExecutive.name || 'Editing Client Executive'}
        actions={
          <ActionList>
            <ActionListItem>
              <Button variant="primary" id="save-button" isDisabled={!isDirty} onClick={onSaveClick}>Save</Button>
            </ActionListItem>
            <ActionListItem>
              <Button variant="link" id="cancel-button" isDisabled={!isDirty} onClick={onCancelClick}>Cancel</Button>
            </ActionListItem>
            <ActionListItem>
              <PageActionsMenu
                items={changeLogItems}
                onRefresh={onRefresh}
                onRemove={onRemove}
                isRemoveDisabled={id === 'new'}
                removeConfirmationMessage={`Confirm removal of "${clientExecutive.name}"?`}
              />
            </ActionListItem>
          </ActionList>
        }
      />

      <PageSection isWidthLimited>
        <Stack hasGutter>
          <Card style={{ padding: '24px' }}>
            <Title headingLevel="h2" size="xl" style={{ marginBottom: '24px' }}>General Information</Title>
            <Form isHorizontal>
              {Object.values(validated).includes('error') && (
                <FormAlert>
                  <Alert variant="danger" title="Fill out all required fields before saving." aria-live="polite" isInline />
                </FormAlert>
              )}

              {/* Standard Text Inputs */}
              {['name', 'username', 'email'].map((field) => (
                <FormGroup key={field} label={field.charAt(0).toUpperCase() + field.slice(1)} isRequired fieldId={field}>
                  <TextInput
                    isRequired
                    type={field === 'email' ? 'email' : 'text'}
                    id={field}
                    name={field}
                    value={(clientExecutive as any)[field] || ''}
                    onChange={(_ev, value) => handleChange(field, value)}
                    validated={validated[field]}
                  />
                </FormGroup>
              ))}

              <FormGroup label="Mobile" fieldId="mobile" isRequired>
                <PhoneInput
                  id="mobile"
                  name="mobile"
                  value={clientExecutive.mobile || '+44'}
                  onChange={handleMobileChange}
                  validated={validated.mobile}
                  isRequired
                />
              </FormGroup>

              {/* Regions Multi-Select with Chips */}
              <FormGroup label="Regions" fieldId="regions">
                <Select
                  aria-label="Select regions"
                  isOpen={isRegionDropdownOpen}
                  selected={clientExecutive.regions?.map(r => r.id) || []}
                  onSelect={onRegionSelect}
                  onOpenChange={(isOpen) => setIsRegionDropdownOpen(isOpen)}
                  toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                    <MenuToggle
                      ref={toggleRef}
                      onClick={() => setIsRegionDropdownOpen(!isRegionDropdownOpen)}
                      isExpanded={isRegionDropdownOpen}
                      style={{ width: '100%' }}
                    >
                      {clientExecutive.regions && clientExecutive.regions.length > 0 ? (
                        <LabelGroup numLabels={8}>
                          {clientExecutive.regions.map(r => (
                            <Label
                              key={r.id}
                              color="grey"
                              onClose={(e) => {
                                e.stopPropagation();
                                onRegionSelect(e, r.id);
                              }}
                            >
                              {r.name}
                            </Label>
                          ))}
                        </LabelGroup>
                      ) : (
                        'Select regions'
                      )}
                    </MenuToggle>
                  )}
                >
                  {redHatRegions.map(region => (
                    <SelectOption
                      hasCheckbox
                      key={region.id}
                      value={region.id}
                      isSelected={clientExecutive.regions?.some(r => r.id === region.id)}
                    >
                      {region.name}
                    </SelectOption>
                  ))}
                </Select>
              </FormGroup>
            </Form>
          </Card>
        </Stack>
      </PageSection>
    </EntityChangeLog>
  );
};

export { ClientExecutiveDetail };
